import { zonedDate } from 'global/helpers/dates'
import i18n from 'i18n'

interface ErrorBaseParams {
  code: string
  status: number
  details: string
}

type ErrorExtraParams = Record<string, string | number>

export interface ErrorDetails extends ErrorBaseParams, ErrorExtraParams {
}

export interface ErrorMessage {
  message: string
  [key: string]: string | number
}

interface LegacyError {
  message: string
}

const genericErrorMessages = (): ErrorMessage[] => [{ message: i18n.t('errors.GenericError') }]

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const isErrorDetails = (value: unknown): value is ErrorDetails =>
  isRecord(value)
  && typeof value['code'] === 'string'
  && typeof value['status'] === 'number'
  && typeof value['details'] === 'string'

const isErrorDetailsList = (value: unknown): value is ErrorDetails[] =>
  Array.isArray(value) && value.length > 0 && value.every(isErrorDetails)

const isLegacyError = (value: unknown): value is LegacyError =>
  isRecord(value)
  && typeof value['message'] === 'string'
  && value['message'].trim().length > 0

const hasNonEmptyMessage = (value: unknown): value is { message: string } =>
  isRecord(value)
  && 'message' in value
  && typeof value['message'] === 'string'
  && value['message'].trim().length > 0

const isErrorMessage = (value: unknown): value is ErrorMessage =>
  hasNonEmptyMessage(value)

export const isErrorMessageArray = (value: unknown): value is ErrorMessage[] =>
  Array.isArray(value)
  && value.length > 0
  && value.every(isErrorMessage)

const processParams = (code: string, params: ErrorExtraParams): ErrorExtraParams => {
  switch (code) {
    case 'WaypointStatusInvalidWaypointType':
      return {
        ...params,
        actualWaypointType: i18n.t(`waypoints.types.${params['actualWaypointType']}`),
        commandWaypointType: i18n.t(`waypoints.types.${params['commandWaypointType']}`),
      }
    case 'WaypointStatusAlreadySet':
    case 'WaypointStatusPrevStatusMissing':
      return {
        ...params,
        waypointStatus: i18n.t(`waypoints.statuses.${params['waypointStatus']}`),
      }
    case 'WaypointStatusFinalStatusAlreadySet':
      return {
        ...params,
        finalStatus: i18n.t(`waypoints.statuses.${params['finalStatus']}`),
        receivedAt: typeof params['receivedAt'] === 'string'
          ? zonedDate(params['receivedAt'], 'full') ?? ''
          : '',
      }
    case 'WaypointStatusInvalidStatus':
      return {
        ...params,
        waypointStatus: i18n.t(`waypoints.statuses.${params['waypointStatus']}`),
        waypointType: i18n.t(`waypoints.types.${params['waypointType']}`),
      }
    case 'InvalidFileType':
      return {
        ...params,
        fileType: i18n.t(`attachments.types.${params['fileType']}`),
        waypointType: i18n.t(`waypoints.types.${params['waypointType']}`),
      }
    case 'FileTypeAlreadyAttached':
      return {
        ...params,
        fileType: i18n.t(`attachments.types.${params['fileType']}`),
      }
  }
  return params
}

export const handleErrorResponse = (errors: unknown): ErrorMessage[] => {
  let errorDetails: ErrorDetails[] | undefined
  if (isErrorDetails(errors)) {
    errorDetails = [errors]
  } else if (isErrorDetailsList(errors)) {
    errorDetails = errors
  }

  if (errorDetails) {
    return errorDetails.map((error) => {
      const { code, status, details, ...params } = error
      const message = i18n.exists(`errors.${code}`)
        ? i18n.t(`errors.${code}`, processParams(code, params))
        : details
      return {
        code,
        ...params,
        message: message.trim().length > 0 ? message : i18n.t('errors.GenericError'),
      }
    })
  }

  if (isLegacyError(errors)) {
    return [{ message: errors.message }]
  }

  return genericErrorMessages()
}

const getSafeTextResponseMessage = (response: Response, responseBody: unknown): string | undefined => {
  if (getResponseMediaType(response) !== 'text/plain' || typeof responseBody !== 'string') {
    return undefined
  }

  const message = responseBody.trim().replace(/\s+/g, ' ')
  if (message.length === 0) {
    return undefined
  }
  return message
}

const getServerErrorMessage = (
  response: Response,
  errors?: ErrorMessage[],
  responseBody?: unknown,
): string => {
  const structuredMessage = errors
    ?.find((error) => typeof error.message === 'string' && error.message.trim().length > 0)
    ?.message.trim()
  const preferredMessage = structuredMessage
    ?? getSafeTextResponseMessage(response, responseBody)
    ?? response.statusText.trim()
  return preferredMessage || `Request failed with status ${response.status}`
}

export class ServerError extends Error {
  constructor(
    public readonly response: Response,
    public readonly errors?: ErrorMessage[],
    responseBody?: unknown,
  ) {
    super(getServerErrorMessage(response, errors, responseBody))
    this.name = 'ServerError'
  }
}

export const isServerError = (error: unknown): error is ServerError =>
  error instanceof ServerError

export const createServerError = async (response: Response): Promise<ServerError> => {
  if (isJsonResponse(response)) {
    let responseBody: unknown
    try {
      responseBody = await response.json()
    } catch {
      responseBody = undefined
    }
    return new ServerError(response, handleErrorResponse(responseBody), responseBody)
  }

  const responseBody = getResponseMediaType(response) === 'text/plain'
    ? await response.text()
    : undefined
  return new ServerError(response, undefined, responseBody)
}

export const getRequestErrorMessages = (error: unknown): ErrorMessage[] => {
  if (isErrorMessageArray(error)) {
    return error
  }

  if (isServerError(error) && error.errors?.length) {
    const structuredErrors = error.errors.filter(hasNonEmptyMessage)
    if (structuredErrors.length > 0) {
      return structuredErrors
    }
  }

  if (error instanceof TypeError || error instanceof SyntaxError) {
    return genericErrorMessages()
  }

  if (hasNonEmptyMessage(error)) {
    return [{ message: error.message }]
  }

  if (typeof error === 'string' && error.trim().length > 0) {
    return [{ message: error }]
  }

  return genericErrorMessages()
}

export const getRequestErrorMessage = (error: unknown): string | undefined => {
  const messages = getRequestErrorMessages(error).map(({ message }) => message)
  return messages.length > 0 ? messages.join(' ') : undefined
}

export const getResponseMediaType = (response: Response): string | undefined =>
  response.headers.get('Content-Type')?.split(';', 1)[0]?.trim().toLowerCase()

export const isJsonResponse = (response: Response): boolean => {
  const mediaType = getResponseMediaType(response)
  return mediaType === 'application/json' || mediaType?.endsWith('+json') === true
}
